import BottomSheet, { BottomSheetBackdrop, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { Pressable, SxProp, View } from 'dripsy';
import { t } from 'i18next';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { UniButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { LayoutContext } from 'contexts/LayoutContext';
import { EarnStackRoutes, EarnStackScreenProps } from '../../earn.navigator';
import { DateRangeProps } from '@/design-system/components/dateRangePicker';
import { useDateRangePickerSelector } from '@/design-system/components/dateRangePicker/useDateRangePickerSelector';
import { BottomSheetHeader } from '@/design-system/components/headers';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import { Calendar } from '@/design-system/components/dateRangePicker/Calendar';
import { Icon } from '@/design-system/components/icons/Icon';

export interface BottomSheetDatePickerProps {
  value: DateRangeProps;
  onChange: (value: DateRangeProps) => void;
  title?: string;
}

export const BottomSheetDatePicker: React.FC<EarnStackScreenProps<EarnStackRoutes.BottomSheetDatePicker>> = ({
  route: {
    params: { value, title, onChange }
  },
  navigation
}) => {
  const bottomSheetRef = useRef<BottomSheet>(null);
  const layout = useContext(LayoutContext);
  const [selectedDates, setSelectedDates] = useState<DateRangeProps>(value);

  const selector = useDateRangePickerSelector({ onValueChange: () => {} });

  const onConfirm = () => {
    onChange(selectedDates);
  };

  const onBack = () => {
    bottomSheetRef.current?.close();
  };

  const onReset = () => {
    selector.onReset();
    setSelectedDates({
      from: null,
      to: null
    });
  };

  // Get dimensions
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);
  // Bottom Sheet on mobile
  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        onClose={() => navigation.goBack()}
        index={0}
        ref={bottomSheetRef}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View sx={styles.bottomsheetHeadHandler} />}>
        <View sx={{ height: 'auto', width, backgroundColor: '$backgroundPrimary', paddingX: '$4' }} onLayout={handleContentLayout}>
          <View sx={styles.header}>
            <Pressable onPress={onBack}>
              <Icon name="cross" width={24} />
            </Pressable>
            <TextTitle semiBold>{title || t('earn-invest::glossary.mobile-filter-date')}</TextTitle>
            <Pressable onPress={onReset}>
              <TextCaption sx={{ color: '$hyperlink' }}>{t('earn-invest::glossary.mobile-reset')}</TextCaption>
            </Pressable>
          </View>
          <View sx={{ pt: '$4', pb: '$5' }}>
            <View sx={{ gap: '$3', mb: '$3', flexDirection: 'row', alignItems: 'center' }}>
              <UniButton
                size="small"
                variant={!selectedDates.from ? ButtonVariant.outline1 : ButtonVariant.outline}
                label={selectedDates.from ? formatDate(selectedDates.from.toString(), { format: DATE_FORMAT.NO_TIME }) : 'Select From'}
                onPress={() => {}}
                wrapperSx={{ flex: 1 }}
              />
              <TextBody>{t('earn-invest::glossary.mobile-filter-date-to')}</TextBody>
              <UniButton
                size="small"
                variant={!selectedDates.to ? ButtonVariant.outline1 : ButtonVariant.outline}
                label={selectedDates.to ? formatDate(selectedDates.to.toString(), { format: DATE_FORMAT.NO_TIME }) : 'Select To'}
                onPress={() => {}}
                wrapperSx={{ flex: 1 }}
              />
            </View>
            <View sx={{}}>
              <Calendar
                side="SINGLE"
                mode="DATE_RANGE"
                selectedDateRange={selectedDates}
                selectedDate={null}
                calendar={selector.calendarLeft}
                onSelectDate={(date) => {
                  if (!date) return;
                  let dates = selectedDates;
                  if (!selectedDates.from && !selectedDates.to) {
                    dates = { from: date, to: null };
                  } else if (selectedDates.from && !selectedDates.to) {
                    if (date.isBefore(selectedDates.from)) {
                      dates = { from: date, to: null };
                    } else {
                      dates = { from: selectedDates.from, to: date };
                    }
                  } else if (selectedDates.from && selectedDates.to) {
                    dates = { from: date, to: null };
                  }
                  setSelectedDates(dates);
                }}
                onPreviousMonth={selector.onPreviousMonth}
                onNextMonth={selector.onNextMonth}
                onPreviousYear={selector.onPreviousYear}
                onNextYear={selector.onNextYear}
              />
            </View>
          </View>
          <View sx={{ mt: '$5', mb: '$4' }}>
            <UniButton
              size="default"
              label={t('earn-invest::glossary.done')}
              onPress={() => {
                onConfirm();
                onBack();
              }}
            />
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

enum StyleName {
  bottomsheetHeadHandler = 'bottomsheetHeadHandler',
  header = 'header'
}

const styles: Record<StyleName, SxProp> = {
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }
};
